// ChartSection.js - Updated with Portal and improved modal handling
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import ChartComponent from '../../ChartComponent/ChartComponent';
import StackableChart from '../../StackableChart/StackableChart';
import Button from '../../Button/Button';
import './ChartSection.css';

// Modal Component with Portal
const ChartModal = ({ 
  isOpen, 
  onClose, 
  title, 
  children, 
  isAnimating 
}) => {
  const modalRef = useRef(null);

  // Handle escape key press
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent background scrolling
      document.body.style.overflow = 'hidden';
      document.body.classList.add('modal-open');
      
      return () => {
        document.removeEventListener('keydown', handleEscape);
        document.body.style.overflow = 'auto';
        document.body.classList.remove('modal-open');
      };
    }
  }, [isOpen, onClose]);

  // Handle click outside modal
  const handleModalBackdropClick = (e) => {
    if (modalRef.current && !modalRef.current.contains(e.target)) {
      onClose();
    }
  };

  if (!isOpen) return null;

  // Render modal using portal to document.body
  return createPortal(
    <div 
      className={`chart-modal-overlay ${isAnimating ? 'modal-closing' : 'modal-opening'}`}
      onClick={handleModalBackdropClick}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.75)',
        backdropFilter: 'blur(8px)',
        zIndex: 999999, // Even higher z-index
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}
    >
      <div 
        className={`chart-modal-content ${isAnimating ? 'modal-closing' : 'modal-opening'}`}
        ref={modalRef}
        style={{
          background: '#ffffff',
          borderRadius: '24px',
          boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
          width: '95vw',
          height: '90vh',
          maxWidth: '1400px',
          maxHeight: '900px',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          backdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          zIndex: 1000000 // Highest z-index
        }}
      >
        {/* Modal header */}
        <div className="chart-modal-header">
          <h2 className="chart-modal-title">{title || 'Chart View'}</h2>
          <button 
            className="chart-modal-close"
            onClick={onClose}
            aria-label="Close modal"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '40px',
              height: '40px',
              border: 'none',
              borderRadius: '12px',
              background: 'rgba(248, 113, 113, 0.1)',
              color: '#ef4444',
              cursor: 'pointer',
              transition: 'all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
            }}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>
        
        {/* Modal content */}
        <div className="chart-modal-body" style={{ flex: 1, padding: '32px', overflow: 'hidden' }}>
          {children}
        </div>
      </div>
    </div>,
    document.body // Render to document body instead of component tree
  );
};

const ChartSection = ({
  data,
  title,
  chartType = 'line',
  isArchived = false,
  isLoading = false,
  showInsights = false,
  insights = [],
  getOption,
  showPeaks = false,
  stackableData = null,
  onChartTypeToggle = null,
  isStackableView = false,
  error = null
}) => {
  console.log('ChartSection rendered with:', {
    isStackableView,
    stackableData: !!stackableData,
    isLoading,
    error,
    dataLength: data?.length || 0,
    title
  });
  const [animationClass, setAnimationClass] = useState('');
  const [insightsVisible, setInsightsVisible] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalAnimating, setIsModalAnimating] = useState(false);
  const chartRef = useRef(null);
  const navigate = useNavigate();
  
  // Apply animation class when isArchived changes
  useEffect(() => {
    if (isArchived) {
      setAnimationClass('chart-entering');
      const timer = setTimeout(() => setAnimationClass(''), 600);
      return () => clearTimeout(timer);
    }
  }, [isArchived]);

  // Handle modal open
  const openModal = () => {
    setIsModalAnimating(true);
    setIsModalOpen(true);
    
    // Hide all dropdowns when modal opens
    const dropdowns = document.querySelectorAll('.dropdown-menu, [role="listbox"], [role="menu"], .select-dropdown');
    dropdowns.forEach(dropdown => {
      dropdown.style.display = 'none';
    });
    
    // Add a small delay to ensure DOM is ready for animation
    setTimeout(() => {
      setIsModalAnimating(false);
    }, 50);
  };

  // Handle modal close
  const closeModal = () => {
    setIsModalAnimating(true);
    
    setTimeout(() => {
      setIsModalOpen(false);
      setIsModalAnimating(false);
      
      // Restore dropdowns when modal closes
      const dropdowns = document.querySelectorAll('.dropdown-menu, [role="listbox"], [role="menu"], .select-dropdown');
      dropdowns.forEach(dropdown => {
        dropdown.style.display = '';
      });
    }, 300); // Match the CSS transition duration
  };

  // Generate a chartId for ChartComponent
  const generateChartId = () => {
    if (title && title.includes('Happy response')) return 'chart4';
    if (chartType === 'bar') return 'chart1';
    if (chartType === 'pie') return 'chart2';
    if (chartType === 'line2') return 'business-chart';
    return 'chart1';
  };

  // Export data to CSV
  const exportToCSV = () => {
    let exportData;

    // Determine which data to export based on view type
    if (isStackableView && stackableData) {
      // For stackable view, extract the devicePerformance array
      exportData = stackableData.devicePerformance || [];
    } else {
      // For regular view, use the data prop
      exportData = data;
    }

    if (!exportData || !Array.isArray(exportData) || exportData.length === 0) return;

    // Handle case where first item might be null/undefined
    const firstValidItem = exportData.find(item => item && typeof item === 'object');
    if (!firstValidItem) return;

    // Convert data to CSV format
    const headers = Object.keys(firstValidItem);
    const csvContent = [
      headers.join(','),
      ...exportData.map(row => {
        if (!row || typeof row !== 'object') return '';
        return headers.map(header => {
          const value = row[header];
          // Escape quotes and wrap in quotes if contains comma
          const stringValue = String(value ?? '');
          return stringValue.includes(',') ? `"${stringValue.replace(/"/g, '""')}"` : stringValue;
        }).join(',');
      }).filter(row => row) // Remove empty rows
    ].join('\n');

    // Create blob and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', `${title || 'chart-data'}.csv`);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className={`modern-chart-container loading ${animationClass}`}>
        <div className="modern-chart-header">
          <div className="chart-title-skeleton"></div>
        </div>
        <div className="chart-content-area">
          <div className="chart-loading-skeleton">
            <div className="loading-shimmer"></div>
            <div className="loading-text">Loading chart data...</div>
          </div>
        </div>
      </div>
    );
  }

  // No data state
  if (!data || data.length === 0) {
    return (
      <div className={`modern-chart-container no-data ${animationClass}`}>
        <div className="modern-chart-header">
          {title && <h3 className="modern-chart-title">{title}</h3>}
        </div>
        <div className="chart-content-area">
          <div className="no-data-placeholder">
            <div className="no-data-icon">📊</div>
            <div className="no-data-text">No data available</div>
            <div className="no-data-subtext">Check back later for insights</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={`modern-chart-container ${isArchived ? 'archived' : ''} ${animationClass}`}>
        {/* Archived badge */}
        {isArchived && (
          <div className="archived-badge">
            <span>📂</span>
            Archived
          </div>
        )}
        
        {/* Chart header */}
        <div className="modern-chart-header">
          <div className="chart-header-left">
            {title && (
              <h3 className="modern-chart-title">
                {title}
                {isArchived && <span className="archived-indicator"> (Archived)</span>}
              </h3>
            )}
          </div>
          
          {/* Header controls - CSV Export, Expand button, Chart type toggle */}
          <div className="chart-header-controls" style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
            {/* CSV Export button */}
            <button
              className="expand-chart-btn"
              onClick={exportToCSV}
              aria-label="Export chart data to CSV"
              title="Export to CSV"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-8-6z"/>
                <path d="M14 2v6h6M8 13h8M8 17h8M8 9h2"/>
              </svg>
            </button>

            {/* Expand button */}
            <button
              className="expand-chart-btn"
              onClick={openModal}
              aria-label="Expand chart"
              title="View in full screen"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"/>
              </svg>
            </button>
            
            {/* Chart type toggle */}
            {(stackableData && onChartTypeToggle) && (
              <div className="chart-type-toggle">
                <button 
                  className={`toggle-btn ${!isStackableView ? 'active' : ''}`}
                  onClick={() => onChartTypeToggle(false)}
                  aria-label="Show time series chart"
                  title="Time Series View"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3 3v18h18V3H3zm16 16H5V5h14v14zM9 17l2-4 2 2 4-6v8H9z"/>
                  </svg>
                </button>
                <button 
                  className={`toggle-btn ${isStackableView ? 'active' : ''}`}
                  onClick={() => onChartTypeToggle(true)}
                  aria-label="Show distribution chart"
                  title="Distribution View"
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3 13h2v8H3v-8zm4-6h2v14H7V7zm4-6h2v20h-2V1zm4 8h2v12h-2V9zm4-4h2v16h-2V5z"/>
                  </svg>
                </button>
              </div>
            )}
          </div>
        </div>
        
        {/* Chart content area */}
        <div className="chart-content-area">
          {/* Conditionally render stackable chart or regular chart */}
          {isStackableView ? (
            <StackableChart
              data={stackableData}
              isLoading={isLoading}
              title={title}
              error={error}
            />
          ) : (
            <div className="chart-wrapper" ref={chartRef}>
              <ChartComponent
                isLoading={false}
                chartId={generateChartId()}
                data={data}
                title={title}
                chartType={chartType}
                getOption={getOption}
                showPeaks={showPeaks}
              />
            </div>
          )}
          
          {/* Insights panel */}
          {showInsights && insights && insights.length > 0 && insightsVisible && (
            <div className="insights-overlay">
              <div className="insights-header">
                <span className="insights-icon">💡</span>
                Key Insights
                <button 
                  className="insights-close-btn"
                  onClick={() => setInsightsVisible(false)}
                  aria-label="Close insights"
                >
                  ✕
                </button>
              </div>
              <div className="insights-list">
                {insights.map((insight, index) => (
                  <div key={index} className={`insight-item priority-${insight.priority || 'low'}`}>
                    <div className="insight-indicator"></div>
                    <span className="insight-text">{insight.text}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        
        {/* Glassmorphism overlay for archived items */}
        {isArchived && <div className="archived-overlay"></div>}
      </div>

      {/* Chart Modal using Portal */}
      <ChartModal 
        isOpen={isModalOpen}
        onClose={closeModal}
        title={title}
        isAnimating={isModalAnimating}
      >
        {isStackableView ? (
          <StackableChart
            data={stackableData}
            isLoading={isLoading}
            title={title}
            error={error}
          />
        ) : (
          <div className="modal-chart-wrapper" style={{
            flex: 1,
            position: 'relative',
            minHeight: 0,
            borderRadius: '16px',
            overflow: 'hidden',
            background: 'rgba(255, 255, 255, 0.4)',
            backdropFilter: 'blur(15px)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
          }}>
            <ChartComponent
              isLoading={false}
              chartId={generateChartId()}
              data={data}
              title={title}
              chartType={chartType}
              getOption={getOption}
              showPeaks={showPeaks}
            />
          </div>
        )}
        
        {/* Modal chart type toggle (if applicable) */}
        {(stackableData && onChartTypeToggle) && (
          <div style={{
            padding: '20px 0 0 0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <div className="chart-type-toggle modal-toggle">
              <button 
                className={`toggle-btn ${!isStackableView ? 'active' : ''}`}
                onClick={() => onChartTypeToggle(false)}
                aria-label="Show time series chart"
                title="Time Series View"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3 3v18h18V3H3zm16 16H5V5h14v14zM9 17l2-4 2 2 4-6v8H9z"/>
                </svg>
              </button>
              <button 
                className={`toggle-btn ${isStackableView ? 'active' : ''}`}
                onClick={() => onChartTypeToggle(true)}
                aria-label="Show distribution chart"
                title="Distribution View"
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3 13h2v8H3v-8zm4-6h2v14H7V7zm4-6h2v20h-2V1zm4 8h2v12h-2V9zm4-4h2v16h-2V5z"/>
                </svg>
              </button>
            </div>
          </div>
        )}
      </ChartModal>
    </>
  );
};

export default ChartSection;